import { Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { TransactionVM, TransactionRowVM } from '../../../../../models/orderModel';
import { AdminService } from '../../../../../services/admin.service';
import { takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ConfirmDeleteComponent } from '../../../users/edit-user/confirm-delete/confirm-delete.component';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { environment } from '../../../../../../environments/environment';
import { TranslocoService } from '@jsverse/transloco';
import { RefundModalComponent } from '../refund-modal/refund-modal.component';

@Component({
  selector: 'app-back-office24-order-transaction-details',
  templateUrl: './back-office24-order-transaction-details.component.html',
  styleUrl: './back-office24-order-transaction-details.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BackOffice24OrderTransactionDetailsComponent implements OnInit, OnDestroy {

  dataSource: MatTableDataSource<TransactionRowVM>;
  displayedColumns: string[] = [
    'productName',
    'amount',
    'vatAmount'
  ];
  baseUrl: string = '';

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  componentDestroyed$: Subject<boolean> = new Subject();
  hasPaymentUrl: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public transaction: TransactionVM,
    public adminService: AdminService,
    public backOfficeService: BackOfficeService,
    public snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<BackOffice24OrderTransactionDetailsComponent>,
    public dialog: MatDialog,
    public translate: TranslocoService
  ) { }

  ngOnInit(): void {
    const identityUrl = JSON.parse(environment.IDENTITY);
    this.baseUrl = identityUrl === '/' ? `${window.location.origin}/` : identityUrl;
    this.initTable();
  }

  initTable() {
    console.log(this.getPayeeReferenceNumber("0FB30318-2338-4D59-9F71-B35C008593A8"));
    this.transaction.payeeReference = this.getPayeeReferenceNumber(this.transaction.transactionId);
    this.dataSource = new MatTableDataSource<TransactionRowVM>(this.transaction.transactionRowVMs);
  }

  getPayeeReferenceNumber(transactionId: string) {
    const bytes = this.guidToByteArray(transactionId);
    if (bytes.length !== 16) return '';
    const base = btoa(String.fromCharCode(...bytes));
    return base.replace(/=/g, '').replace(/\+/g, 'A').replace(/\//g, 'B');
  }

  private guidToByteArray(guid: string): Uint8Array {
    const hex = guid.replace(/-/g, '').toLowerCase();
    if (hex.length !== 32) {
      return new Uint8Array();
    }
    const toBytes = (h: string) => h.match(/../g)!.map(b => parseInt(b, 16));

    // .NET Guid.ToByteArray uses little-endian for the first 3 fields (Data1, Data2, Data3)
    const d1 = toBytes(hex.slice(0, 8)).reverse();       // 4 bytes, reversed
    const d2 = toBytes(hex.slice(8, 12)).reverse();      // 2 bytes, reversed
    const d3 = toBytes(hex.slice(12, 16)).reverse();     // 2 bytes, reversed
    const d4 = toBytes(hex.slice(16, 20));               // 2 bytes, as-is
    const d5 = toBytes(hex.slice(20, 32));               // 6 bytes, as-is

    return new Uint8Array([...d1, ...d2, ...d3, ...d4, ...d5]);
  }

  orderPaymentCapture(orderId: string) {
    this.backOfficeService
      .orderPaymentCapture(orderId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(order => {
        this.snackBar.open(`Order ${order.orderNr} Captured`, 'OK', {
          duration: 5000,
          verticalPosition: 'top'
        });
      });
  }

  orderPaymentModal(transactionId: string) {
    const dialogRef = this.dialog.open(RefundModalComponent, {
      width: '450px',
      data: { maxAmount: this.transaction.amountIncVat }
    });

    dialogRef.afterClosed().subscribe((result: { type: 'full' | 'partial', amount: number } | null) => {
      if (result) {
        if (result.type === 'full') {
          this.orderPaymentReversal(transactionId);
        } else if (result.type === 'partial') {
          this.orderPaymentReversalPartial(transactionId, result.amount);
        }
      }
    });
  }

  orderPaymentReversal(transactionId: string) {
    const message = this.translate.translate('ORDER_DETAILS.CONFIRM_REFUND_AMOUNT', { amount: this.transaction.amountIncVat });
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: message,
        buttonText: {
          ok: this.translate.translate('PARKIFLY.FORM.YES'),
          cancel: this.translate.translate('FORM.CANCEL')
        }
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.backOfficeService
          .orderPaymentReversal(transactionId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(transaction => {
            this.snackBar.open(`Transaction ${transaction.transactionNr} Reversed`, 'OK', {
              duration: 2000,
              verticalPosition: 'top'
            });
            this.transaction = transaction;
            this.transaction.payeeReference = this.getPayeeReferenceNumber(this.transaction.transactionId);
          });
      }
    });
  }

  orderPaymentReversalPartial(transactionId: string, amount: number) {
    const message = this.translate.translate('ORDER_DETAILS.CONFIRM_REFUND_AMOUNT', { amount: amount });
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: message,
        buttonText: {
          ok: this.translate.translate('PARKIFLY.FORM.YES'),
          cancel: this.translate.translate('FORM.CANCEL')
        }
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.backOfficeService
          .orderPaymentReversalPartial(transactionId, amount)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(transaction => {
            this.snackBar.open(`Transaction ${transaction.transactionNr} reversed for ${amount}SEK`, 'OK', {
              duration: 2000,
              verticalPosition: 'top'
            });
            this.transaction = transaction;
            this.transaction.payeeReference = this.getPayeeReferenceNumber(this.transaction.transactionId);
          });
      }
    });
  }

  orderPaymentReversalTransactionRow(transactionId: string, transactionRowId: string, amount: number) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill återbetala ${amount}SEK?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.backOfficeService
          .orderPaymentReversalTransactionRow(transactionId, transactionRowId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(transaction => {
            this.snackBar.open(`TransactionRow Reversed`, 'OK', {
              duration: 2000,
              verticalPosition: 'top'
            });
            this.transaction = transaction;
            this.transaction.payeeReference = this.getPayeeReferenceNumber(this.transaction.transactionId);
          });
      }
    });
  }

  getTransactionStatus(transactionId: string) {
    this.backOfficeService
      .getTransactionStatus(transactionId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(transaction => {
        this.snackBar.open(`Transaction ${transaction.transactionNr} updated`, 'OK', {
          duration: 2000,
          verticalPosition: 'top'
        });
        this.transaction = transaction;
        this.transaction.payeeReference = this.getPayeeReferenceNumber(this.transaction.transactionId);
      });
  }

  deleteTransaction(orderId: string, transactionId: string) {

    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill radera transaktionen?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {

      if (confirmed) {
        this.backOfficeService
          .deleteTransaction(orderId, transactionId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(order => {

            this.snackBar.open(`Transaktionen är raderad`, 'OK', {
              duration: 2000,
              verticalPosition: 'top'
            });

            this.closeDialog(null);

          });
      }

    });
  }

  copyPaymentUrl() {
    if (this.transaction?.paymentStatus?.toLowerCase() === 'checkout') {
      const paymentUrl = `${this.baseUrl}parkifly/flygets/checkout/payment/${this.transaction.orderId}`;
      navigator.clipboard.writeText(paymentUrl).then(() => {
        this.snackBar.open('Payment URL copied to clipboard', 'OK', {
          duration: 2000,
          verticalPosition: 'top'
        });
      });
    }
  }

  closeDialog(returnData: any) {
    this.dialogRef.close(returnData);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  flatten(obj: any) {
    return Object.keys(obj).reduce((acc, current) => {
      const _key = `${current}`;
      const currentValue = obj[current];
      if (Array.isArray(currentValue) || Object(currentValue) === currentValue) {
        Object.assign(acc, this.flatten(currentValue));
      } else {
        acc[_key] = currentValue;
      }
      return acc;
    }, {});
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
